import { Currency, ReceiptFieldsFragment } from "@/gql/graphql";
import { Badge } from "@/components/catalyst/badge";
import { formatCurrency } from "@/lib/Formatters/formatCurrency";
import { formatDate } from "@/lib/Formatters/formatDate";
import {
  AlertTriangleIcon,
  CalendarIcon,
  ImageIcon,
  ReceiptIcon,
} from "lucide-react";
import { FilePreview, FileThumbnail } from "@/app/Media/Components/FilePreview";
import { useDialog } from "@/lib/Components/Dialog/Hooks/useDialog";
import { BaseDialog } from "@/lib/Components/Dialog/BaseDialog";
import { ReactNode } from "react";
import { useTranslations } from "use-intl";

type ReceiptCardWarnings = {
  hasDateWarning?: boolean;
  hasCurrencyMismatch?: boolean;
  hasDuplicateDate?: boolean;
  bookingCurrency?: Currency;
};

function getReceiptTypeBadgeColor(
  type: ReceiptFieldsFragment["type"],
): "blue" | "purple" | "amber" | "zinc" {
  switch (type) {
    case "FUEL":
      return "blue";
    case "FERRY":
      return "purple";
    case "FLIGHT":
      return "amber";
    case "OTHER":
      return "zinc";
    default:
      return "zinc";
  }
}

function ReceiptImageDialog({
  image,
}: {
  image: NonNullable<ReceiptFieldsFragment["image"]>;
}) {
  return (
    <BaseDialog title="Receipt Image" size="2xl">
      <div className="flex items-center justify-center">
        <FilePreview
          alt="Receipt"
          media={image}
          className="max-h-[70vh] w-auto rounded-lg"
        />
      </div>
    </BaseDialog>
  );
}

export function ReceiptCard({
  receipt,
  warnings,
  actions,
}: {
  receipt: ReceiptFieldsFragment;
  warnings?: ReceiptCardWarnings;
  actions?: ReactNode;
}) {
  const { open: openImageDialog } = useDialog(ReceiptImageDialog);
  const t = useTranslations("refund");

  const receiptCurrency = receipt.currency ?? Currency.Aud;
  const hasMissingFields = !receipt.date || receipt.amount_inc_tax === 0;
  const hasNoTax = receipt.amount_tax === 0;
  const hasPartialClaim = receipt.claimable_percentage < 1;
  const hasWarning =
    warnings?.hasDateWarning ||
    hasMissingFields ||
    warnings?.hasCurrencyMismatch ||
    warnings?.hasDuplicateDate ||
    hasNoTax ||
    hasPartialClaim;

  return (
    <div
      className={`rounded-lg border bg-white p-4 dark:bg-zinc-900 ${
        hasWarning
          ? "border-amber-300 dark:border-amber-700"
          : "border-zinc-200 dark:border-zinc-700"
      }`}
    >
      <div className="flex items-start gap-3">
        {receipt.image ? (
          <button
            type="button"
            onClick={() => openImageDialog({ image: receipt.image! })}
            className="group relative size-16 shrink-0 overflow-hidden rounded-lg border bg-zinc-100 transition-all hover:ring-2 hover:ring-blue-500 hover:ring-offset-2 dark:bg-zinc-800"
          >
            <FileThumbnail
              alt={`Receipt ${receipt.id}`}
              media={receipt.image}
              className="size-full object-cover"
            />
            <div className="absolute inset-0 flex items-center justify-center bg-black/0 transition-colors group-hover:bg-black/20">
              <ImageIcon className="size-4 text-white opacity-0 transition-opacity group-hover:opacity-100" />
            </div>
          </button>
        ) : (
          <div className="flex size-16 shrink-0 items-center justify-center rounded-lg border bg-zinc-100 dark:bg-zinc-800">
            <ReceiptIcon className="size-6 text-zinc-400" />
          </div>
        )}

        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between">
            <Badge color={getReceiptTypeBadgeColor(receipt.type)}>
              {t(`receiptType.${receipt.type}`)}
            </Badge>
            <div className="text-right">
              {receipt.claimable_percentage < 1 ? (
                <>
                  <div className="text-sm text-zinc-400 line-through">
                    {formatCurrency(receipt.amount_inc_tax, receiptCurrency)}
                  </div>
                  <div className="font-semibold">
                    {formatCurrency(
                      receipt.claimable_amount_inc_tax,
                      receiptCurrency,
                    )}
                  </div>
                  <div className="text-xs text-amber-600">
                    {Math.round(receipt.claimable_percentage * 100)}% claimable
                  </div>
                </>
              ) : (
                <div className="font-semibold">
                  {formatCurrency(receipt.amount_inc_tax, receiptCurrency)}
                </div>
              )}
            </div>
          </div>

          {receipt.date ? (
            <div className="mt-2 flex items-center gap-1 text-xs text-zinc-500">
              <CalendarIcon className="size-3" />
              {formatDate(receipt.date)}
            </div>
          ) : null}

          {receipt.description ? (
            <div className="mt-1 text-xs text-zinc-600 dark:text-zinc-400">
              {receipt.description}
            </div>
          ) : null}
        </div>
      </div>

      {hasWarning ? (
        <div className="mt-3 space-y-1 border-t border-zinc-100 pt-3 dark:border-zinc-800">
          {warnings?.hasDateWarning ? (
            <div className="flex items-center gap-1.5 text-xs text-amber-600">
              <AlertTriangleIcon className="size-3" />
              Date outside booking period
            </div>
          ) : null}
          {!receipt.date ? (
            <div className="flex items-center gap-1.5 text-xs text-amber-600">
              <AlertTriangleIcon className="size-3" />
              Missing date
            </div>
          ) : null}
          {warnings?.hasDuplicateDate ? (
            <div className="flex items-center gap-1.5 text-xs text-amber-600">
              <AlertTriangleIcon className="size-3" />
              Multiple receipts on this date — verify these are not duplicates
            </div>
          ) : null}
          {receipt.amount_inc_tax === 0 ? (
            <div className="flex items-center gap-1.5 text-xs text-amber-600">
              <AlertTriangleIcon className="size-3" />
              Missing amount
            </div>
          ) : null}
          {warnings?.hasCurrencyMismatch ? (
            <div className="flex items-center gap-1.5 text-xs text-amber-600">
              <AlertTriangleIcon className="size-3" />
              Currency mismatch ({receiptCurrency} vs {warnings.bookingCurrency}
              )
            </div>
          ) : null}
          {hasNoTax ? (
            <div className="flex items-center gap-1.5 text-xs text-amber-600">
              <AlertTriangleIcon className="size-3" />
              No tax
            </div>
          ) : null}
          {hasPartialClaim ? (
            <div className="flex items-center gap-1.5 text-xs text-amber-600">
              <AlertTriangleIcon className="size-3" />
              Only {Math.round(receipt.claimable_percentage * 100)}% claimable —
              receipt contains non-claimable items
            </div>
          ) : null}
        </div>
      ) : null}

      {actions ? (
        <div className="mt-3 flex items-center justify-end gap-1 border-t border-zinc-100 pt-3 dark:border-zinc-800">
          {actions}
        </div>
      ) : null}
    </div>
  );
}
